import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { autoLoginWithToken, getLoggedInUser } from "../../api/loginAPI";
import { fetchUsersByEmail } from "../../api/userAPI";
import { useRole } from "../../context/RoleContext";
import { routes } from "../../routes/routes";
import { CustomButton } from "../../common/FormElements";
import useIsMobile from "../../common/mobileVersion/useIsMobile";
import { getOrganizationId, setOrganizationId } from "../../utils/orgIdUtils";
import {
  getHeadingStyle,
  getSubTextStyle,
} from "../loginScreen/LoginScreen.styles";
import { normalizeRole } from "../../utils/roleUtils";

const AutoLogin: React.FC = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { setRole } = useRole();
  const [error, setError] = useState<string>("");
  const isMobile = useIsMobile();

  useEffect(() => {
    console.log("AutoLogin component loaded");
    console.log("Current URL:", window.location.href);
    console.log("Search params:", Object.fromEntries(searchParams.entries()));

    const token = searchParams.get("token");
    const orgId = getOrganizationId();

    console.log("Extracted token:", token);
    console.log("OrgId:", orgId);

    if (!token) {
      console.error("Token is missing from URL params");
      setError("Invalid login link. Token is missing.");
      return;
    }

    const performAutoLogin = async () => {
      console.log("Starting performAutoLogin function");
      try {
        // Clear old session data before auto-login
        const preservedOrgId = getOrganizationId();
        console.log("Clearing session data, preserving orgId:", preservedOrgId);
        localStorage.clear();
        sessionStorage.clear();
        if (preservedOrgId) {
          setOrganizationId(preservedOrgId);
        }

        console.log(
          "Calling autoLoginWithToken with token:",
          token.substring(0, 20) + "..."
        );
        const result = await autoLoginWithToken(token);
        console.log("autoLoginWithToken response:", result);

        if (!result?.data?.tokens?.accessToken) {
          setError(result?.message || "Auto-login failed.");
          return;
        }

        localStorage.setItem("accessToken", result.data.tokens.accessToken);
        localStorage.setItem("idToken", result.data.tokens.idToken);
        const userType = normalizeRole(result.data.type);
        localStorage.setItem("userRole", userType);
        localStorage.setItem("userEmail", result.data.email);
        sessionStorage.setItem("refreshToken", result.data.tokens.refreshToken);

        if (result.data.organizationId) {
          setOrganizationId(result.data.organizationId);
        }

        const userEmail = result.data.email;
        setRole(userType);

        try {
          const response = await fetchUsersByEmail({ emailId: userEmail });
          const sellerName = response?.data?.data?.seller?.name || "";
          localStorage.setItem("Name", sellerName);
        } catch {
          localStorage.setItem("Name", "");
        }

        // Store trial data if available (from auto-login response)
        if (result.data.trial?.daysRemaining !== undefined) {
          console.log(
            "Storing trialDaysRemaining:",
            result.data.trial.daysRemaining
          );
          localStorage.setItem(
            "trialDaysRemaining",
            result.data.trial.daysRemaining.toString()
          );
        } else {
          console.log(
            "No trial data found in auto-login response:",
            result.data
          );
        }

        // Check if this is first login and redirect accordingly
        if (result.data.isFirstLogin) {
          console.log("First login detected, redirecting to first-sign-in");
          setTimeout(() => navigate(`/maltora-seller/first-sign-in`), 100);
        } else {
          console.log("Existing user, redirecting to dashboard");
          setTimeout(() => navigate(`/maltora-seller/dashboard/sales`), 100);
        }
      } catch (err: unknown) {
        const error = err as { response?: { data?: { message?: string } } };
        const message =
          error?.response?.data?.message ||
          "Auto-login failed. The link may have expired.";
        setError(message);
      }
    };

    console.log("About to call performAutoLogin...");
    performAutoLogin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          gap: 2,
          padding: isMobile ? "24px" : "48px",
        }}
      >
        <Typography sx={getHeadingStyle(isMobile)}>Login Failed</Typography>
        <Typography sx={getSubTextStyle(isMobile)}>{error}</Typography>
        <CustomButton
          label="Go to Sign In"
          variant="contained"
          fullWidth={false}
          onClick={() => navigate(`/${routes.public.signIn}`)}
          sx={{ width: isMobile ? "100%" : "300px" }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
        gap: 2,
        padding: isMobile ? "24px" : "48px",
      }}
    >
      <Typography sx={getSubTextStyle(isMobile)}>Logging you in...</Typography>
    </Box>
  );
};

export default AutoLogin;
